import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../auth/useAuth';
import { startLinkWallet, confirmLinkWallet, getLinkedWallets } from '../services/kip';
import type { LinkedWallets } from '../services/kip';

interface ConnectWalletModalProps {
  isOpen: boolean;
  onClose: () => void;
  showLinkWallets?: boolean; // If true, show link wallet options; if false, show login options
}

type WalletKind = 'ii' | 'plug' | 'oisy' | 'phantom' | 'sui';

interface WalletOption {
  id: WalletKind;
  name: string;
  icon: string;
  description: string;
  available: boolean;
  isLogin?: boolean; // If true, this is a login option; if false, it's a link option
}

// Check if Phantom is installed
function isPhantomInstalled(): boolean {
  return typeof window !== 'undefined' && typeof (window as any).solana !== 'undefined' && (window as any).solana.isPhantom;
}

// Check if Sui wallet is installed
function isSuiWalletInstalled(): boolean {
  return typeof window !== 'undefined' && typeof (window as any).suiWallet !== 'undefined';
}

// Check if Plug is installed
function isPlugInstalled(): boolean {
  return typeof window !== 'undefined' && typeof (window as any).ic?.plug !== 'undefined';
}

// Check if OISY is available
function isOisyAvailable(): boolean {
  return typeof window !== 'undefined' && typeof (window as any).ic?.oisy !== 'undefined';
}

const loginOptions: WalletOption[] = [
  {
    id: 'ii',
    name: 'Internet Identity',
    icon: '🌐',
    description: 'Secure, anonymous authentication by DFINITY',
    available: true,
    isLogin: true,
  },
  {
    id: 'plug',
    name: 'Plug Wallet',
    icon: '🔌',
    description: 'Browser extension wallet for ICP',
    available: isPlugInstalled(),
    isLogin: true,
  },
  {
    id: 'oisy',
    name: 'OISY Wallet',
    icon: '💎',
    description: 'Multi-chain wallet on Internet Computer',
    available: isOisyAvailable(),
    isLogin: true,
  },
];

const linkOptions: WalletOption[] = [
  {
    id: 'phantom',
    name: 'Phantom',
    icon: '👻',
    description: 'Link your Solana wallet',
    available: isPhantomInstalled(),
    isLogin: false,
  },
  {
    id: 'sui',
    name: 'Sui Wallet',
    icon: '💧',
    description: 'Link your Sui wallet',
    available: isSuiWalletInstalled(),
    isLogin: false,
  },
];

export default function ConnectWalletModal({ isOpen, onClose, showLinkWallets = false }: ConnectWalletModalProps) {
  const { isAuthenticated, principal, agent, loginWithII, loginWithPlug, loginWithOisy } = useAuth();
  const [connecting, setConnecting] = useState<WalletKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linkedWallets, setLinkedWallets] = useState<LinkedWallets | null>(null);

  const options = showLinkWallets ? linkOptions : loginOptions;

  useEffect(() => {
    if (isOpen && isAuthenticated && agent && principal && showLinkWallets) {
      loadLinkedWallets();
    }
  }, [isOpen, isAuthenticated, agent, principal, showLinkWallets]);

  const loadLinkedWallets = async () => {
    if (!agent || !principal) return;
    try {
      const wallets = await getLinkedWallets(agent, principal);
      setLinkedWallets(wallets);
    } catch (err) {
      console.error('Failed to load linked wallets:', err);
    }
  };

  const handleLogin = async (kind: WalletKind) => {
    if (kind !== 'ii' && kind !== 'plug' && kind !== 'oisy') {
      setError('Invalid login option');
      return;
    }

    setConnecting(kind);
    setError(null);

    try {
      if (kind === 'ii') {
        await loginWithII();
      } else if (kind === 'plug') {
        await loginWithPlug();
      } else if (kind === 'oisy') {
        await loginWithOisy();
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to connect wallet');
    } finally {
      setConnecting(null);
    }
  };

  const handleLinkWallet = async (kind: WalletKind) => {
    if (!agent || !principal) {
      setError('Please login first');
      return;
    }

    if (kind !== 'phantom' && kind !== 'sui') {
      setError('Invalid link option');
      return;
    }

    setConnecting(kind);
    setError(null);

    try {
      // Start the link challenge
      const challenge = await startLinkWallet(agent, kind);

      let address: string;
      let signature: string;

      if (kind === 'phantom') {
        // Connect to Phantom and sign the message
        const phantom = (window as any).solana;
        if (!phantom) {
          throw new Error('Phantom wallet not found');
        }

        const response = await phantom.connect();
        address = response.publicKey.toString();

        // Sign the challenge message
        const encodedMessage = new TextEncoder().encode(challenge.message);
        const signedMessage = await phantom.signMessage(encodedMessage, 'utf8');
        // Convert Uint8Array to base64
        const signatureBytes = new Uint8Array(signedMessage.signature);
        signature = btoa(String.fromCharCode(...signatureBytes));
      } else if (kind === 'sui') {
        // Connect to Sui wallet and sign the message
        const suiWallet = (window as any).suiWallet;
        if (!suiWallet) {
          throw new Error('Sui wallet not found');
        }

        const accounts = await suiWallet.getAccounts();
        if (accounts.length === 0) {
          throw new Error('No Sui accounts found');
        }
        address = accounts[0];

        // Sign the challenge message (Sui uses Ed25519)
        const encodedMessage = new TextEncoder().encode(challenge.message);
        const signedMessage = await suiWallet.signMessage({
          message: encodedMessage,
        });
        // Convert Uint8Array to base64
        const signatureBytes = new Uint8Array(signedMessage.signature);
        signature = btoa(String.fromCharCode(...signatureBytes));
      } else {
        throw new Error('Unsupported wallet type');
      }

      // Confirm the link on the backend
      await confirmLinkWallet(agent, kind, address, signature, challenge.nonce);

      // Reload linked wallets
      await loadLinkedWallets();
      setConnecting(null);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to link wallet');
      setConnecting(null);
    }
  };

  const handleClick = async (option: WalletOption) => {
    if (!option.available) {
      setError(`${option.name} is not installed. Please install it first.`);
      return;
    }

    if (showLinkWallets) {
      await handleLinkWallet(option.id);
    } else {
      await handleLogin(option.id);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative bg-gradient-to-b from-gray-900 to-black border border-gold/30 rounded-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Header */}
          <h2 className="text-3xl font-bold mb-2 text-gold">
            {showLinkWallets ? 'Link External Wallets' : 'Connect Wallet'}
          </h2>
          <p className="text-gray-400 mb-6">
            {showLinkWallets
              ? 'Link your external wallets to your account'
              : 'Choose a wallet to connect to the Forge'}
          </p>

          {/* Error message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-red-200 text-sm"
            >
              {error}
            </motion.div>
          )}

          {/* Wallet options */}
          <div className="space-y-3 mb-6">
            {options.map((option) => {
              const isConnecting = connecting === option.id;
              const isLinked =
                showLinkWallets &&
                linkedWallets &&
                ((option.id === 'phantom' && linkedWallets.solana_pubkeys.length > 0) ||
                  (option.id === 'sui' && linkedWallets.sui_addresses.length > 0));

              return (
                <motion.button
                  key={option.id}
                  onClick={() => handleClick(option)}
                  disabled={!option.available || isConnecting}
                  whileHover={option.available && !isConnecting ? { scale: 1.02 } : {}}
                  whileTap={option.available && !isConnecting ? { scale: 0.98 } : {}}
                  className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                    !option.available
                      ? 'bg-gray-800/50 border-gray-700 text-gray-500 cursor-not-allowed'
                      : isLinked
                      ? 'bg-gold/20 border-gold text-gold'
                      : isConnecting
                      ? 'bg-gray-800 border-gold/50 text-gray-300'
                      : 'bg-gray-800/50 border-gray-600 hover:border-gold hover:bg-gray-800 text-white'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-3xl">{option.icon}</span>
                      <div>
                        <div className="font-semibold">{option.name}</div>
                        <div className="text-sm text-gray-400">{option.description}</div>
                      </div>
                    </div>
                    <div>
                      {isConnecting ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-gold border-t-transparent" />
                      ) : isLinked ? (
                        <span className="text-gold">✓ Linked</span>
                      ) : !option.available ? (
                        <span className="text-xs text-gray-500">Not installed</span>
                      ) : null}
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Linked wallets display (when showing link options) */}
          {showLinkWallets && linkedWallets && (
            <div className="mt-6 pt-6 border-t border-gray-700">
              <h3 className="text-lg font-semibold mb-3 text-gold">Linked Wallets</h3>
              {linkedWallets.solana_pubkeys.length > 0 && (
                <div className="mb-2">
                  <span className="text-gray-400">Solana:</span>
                  <div className="text-sm font-mono text-gray-300">
                    {linkedWallets.solana_pubkeys.map((addr, i) => (
                      <div key={i}>{addr}</div>
                    ))}
                  </div>
                </div>
              )}
              {linkedWallets.sui_addresses.length > 0 && (
                <div className="mb-2">
                  <span className="text-gray-400">Sui:</span>
                  <div className="text-sm font-mono text-gray-300">
                    {linkedWallets.sui_addresses.map((addr, i) => (
                      <div key={i}>{addr}</div>
                    ))}
                  </div>
                </div>
              )}
              {linkedWallets.evm_addresses.length > 0 && (
                <div>
                  <span className="text-gray-400">EVM:</span>
                  <div className="text-sm font-mono text-gray-300">
                    {linkedWallets.evm_addresses.map((addr, i) => (
                      <div key={i}>{addr}</div>
                    ))}
                  </div>
                </div>
              )}
              {linkedWallets.solana_pubkeys.length === 0 &&
                linkedWallets.sui_addresses.length === 0 &&
                linkedWallets.evm_addresses.length === 0 && (
                  <p className="text-gray-400 text-sm">No wallets linked yet</p>
                )}
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
